import { PayloadAction, createSlice, isAnyOf } from '@reduxjs/toolkit';
import dayjs from 'dayjs';

import { CategoriesResponse } from '@/core/interfaces/categories';
import { CountriesWithWatersResponse } from '@/core/interfaces/countries';

import {
  AnalysisAlertSettingItem,
  AnalysisAlertSettingPreference,
  FocusAlertItem,
  FocusesState,
  FocusNotificationDayOfWeek,
  FocusResponseItem,
  FocusType,
} from '@/features/Focus/interfaces';
import {
  createAnalysisAlertSetting,
  createFocus,
  createFocusAlert,
  deleteFocus,
  deleteFocusAlert,
  getFocusesList,
  getSharedFocusesList,
  updateAnalysisAlertSetting,
  updateFocus,
  updateFocusAlert,
} from '@/features/Focus/store';
import { daysForWeekdays, transformFocusDataToAppliedFilters } from '@/features/Focus/utils';

function toFocusNotificationDayOfWeek(str: string): FocusNotificationDayOfWeek | null {
  const found = Object.values(FocusNotificationDayOfWeek).find(day => day === str);

  return found ?? null;
}

const initialState: FocusesState = {
  selectedFocus: null,
  focuses: [],
  sharedFocuses: [],
  focusModal: null,
  draftFocus: null,
};

const mapFocusDataToFocus = (
  focusData: FocusResponseItem,
  {
    alerts = [],
    analysisAlertSettings = [],
    categories,
    countries,
  }: {
    alerts?: Array<FocusAlertItem>;
    analysisAlertSettings?: Array<AnalysisAlertSettingItem>;
    categories: CategoriesResponse;
    countries: CountriesWithWatersResponse;
  }
): FocusType => {
  const focusAlert = alerts.find(alert => alert.focus === focusData.id);
  const analysisAlert = analysisAlertSettings.find(a => a.focus === focusData.id);
  let alertTime = dayjs().utc();
  let alertTimeInLocal: dayjs.Dayjs | null = null;

  if (focusAlert?.timeOfNotification) {
    const emailFrequencyDay = focusAlert.dayOfTheWeek
      ? daysForWeekdays.indexOf(focusAlert.dayOfTheWeek)
      : undefined;
    const [emailFrequencyHour, emailFrequencyMinutes] = focusAlert?.timeOfNotification.split(':');

    if (emailFrequencyDay !== undefined) {
      alertTime = alertTime.day(emailFrequencyDay);
    }

    if (emailFrequencyHour && emailFrequencyMinutes) {
      alertTime = alertTime.hour(Number(emailFrequencyHour)).minute(Number(emailFrequencyMinutes));
    }

    alertTimeInLocal = alertTime.local();
  }

  let aiAnalystTime: string | undefined;
  let aiAnalystDay: FocusNotificationDayOfWeek | undefined;

  if (analysisAlert?.timeOfNotification) {
    const timeMatch = analysisAlert.timeOfNotification.match(/^(\d{1,2}):(\d{1,2})/);

    if (timeMatch) {
      const utcDayIndex = analysisAlert.dayOfTheWeek
        ? daysForWeekdays.indexOf(analysisAlert.dayOfTheWeek)
        : undefined;
      let aiAlertTime = dayjs().utc();

      if (utcDayIndex !== undefined && utcDayIndex !== -1) {
        aiAlertTime = aiAlertTime.day(utcDayIndex);
      }

      aiAlertTime = aiAlertTime.hour(Number(timeMatch[1])).minute(Number(timeMatch[2]));
      const aiAlertTimeLocal = aiAlertTime.local();

      aiAnalystTime = aiAlertTimeLocal.format('HH:mm');
      aiAnalystDay = toFocusNotificationDayOfWeek(aiAlertTimeLocal.format('dddd')) ?? undefined;
    }
  }

  return {
    id: focusData.id,
    name: focusData.name,
    description: focusData.description,
    filters: transformFocusDataToAppliedFilters({
      focusData,
      categories,
      countries,
    }),
    emailNotifications: !!focusAlert?.receiveEmail,
    emailFrequency: focusAlert?.receiveEmail
      ? {
          day: alertTimeInLocal
            ? toFocusNotificationDayOfWeek(alertTimeInLocal.format('dddd'))
            : null,
          frequency: focusAlert.notificationPreference,
          hour: alertTimeInLocal ? alertTimeInLocal.format('HH:mm') : null,
        }
      : undefined,
    hasAnalysisAlertSetting: !!analysisAlert,
    aiAnalystReports: !!analysisAlert?.receiveEmail,
    aiAnalystFrequency: analysisAlert?.notificationPreference,
    aiAnalystTime,
    aiAnalystDay,
    users: focusData.users,
    organizations: focusData.organizations,
  };
};

export const focusesSlice = createSlice({
  name: 'filters',
  initialState,
  reducers: {
    openFocusModal: (state, { payload }: PayloadAction<'edit' | 'create' | null>) => {
      state.focusModal = payload;
    },
    selectFocus: (state, { payload }: PayloadAction<number | null>) => {
      const focusId = payload;

      const foundFocus =
        state.focuses.find(focus => focus.id === focusId) ||
        state.sharedFocuses.find(focus => focus.id === focusId);

      state.selectedFocus = foundFocus || null;
    },
    saveDraftFocus: (state, { payload }: PayloadAction<FocusesState['draftFocus']>) => {
      state.draftFocus = payload;
    },
    clearDraftFocus: state => {
      state.draftFocus = null;
    },
    updateFocusAiAnalystSettings: (
      state,
      {
        payload,
      }: PayloadAction<{
        focusId: number;
        hasAnalysisAlertSetting?: boolean;
        aiAnalystReports: boolean;
        aiAnalystFrequency?: AnalysisAlertSettingPreference;
        aiAnalystTime?: string;
        aiAnalystDay?: FocusNotificationDayOfWeek;
      }>
    ) => {
      const focusItem =
        state.focuses.find(focus => focus.id === payload.focusId) ||
        state.sharedFocuses.find(focus => focus.id === payload.focusId);

      if (focusItem) {
        if (payload.hasAnalysisAlertSetting !== undefined) {
          focusItem.hasAnalysisAlertSetting = payload.hasAnalysisAlertSetting;
        }
        focusItem.aiAnalystReports = payload.aiAnalystReports;

        if (payload.aiAnalystFrequency !== undefined) {
          focusItem.aiAnalystFrequency = payload.aiAnalystFrequency;
        }

        if (payload.aiAnalystTime !== undefined) {
          focusItem.aiAnalystTime = payload.aiAnalystTime;
        }

        if (payload.aiAnalystDay !== undefined) {
          focusItem.aiAnalystDay = payload.aiAnalystDay;
        }
      }
    },
  },
  extraReducers: builder =>
    builder
      .addCase(
        createFocus.fulfilled,
        (state, { payload: { focusData, alerts, categories, countries } }) => {
          state.focuses = [
            ...state.focuses,
            mapFocusDataToFocus(focusData, {
              alerts,
              categories,
              countries,
            }),
          ];
        }
      )
      .addCase(
        updateFocus.fulfilled,
        (state, { payload: { focusData, alerts, categories, countries } }) => {
          const isSharedFocus = state.sharedFocuses.some(focus => focus.id === focusData.id);
          const isMyFocus = state.focuses.some(focus => focus.id === focusData.id);

          if (isSharedFocus) {
            state.sharedFocuses = state.sharedFocuses.map(focus => {
              if (focus.id === focusData.id) {
                return mapFocusDataToFocus(focusData, {
                  alerts,
                  categories,
                  countries,
                });
              }

              return focus;
            });
          }

          if (isMyFocus) {
            state.focuses = state.focuses.map(focus => {
              if (focus.id === focusData.id) {
                return mapFocusDataToFocus(focusData, {
                  alerts,
                  categories,
                  countries,
                });
              }

              return focus;
            });
          }
        }
      )
      .addCase(deleteFocus.fulfilled, (state, { payload }) => {
        state.focuses = state.focuses.filter(focus => focus.id !== payload);
        state.sharedFocuses = state.sharedFocuses.filter(focus => focus.id !== payload);
      })
      .addCase(
        getFocusesList.fulfilled,
        (state, { payload: { alerts, analysisAlertSettings, categories, countries, focuses } }) => {
          state.focuses = focuses.map(focus =>
            mapFocusDataToFocus(focus, {
              alerts,
              analysisAlertSettings,
              categories,
              countries,
            })
          );
        }
      )
      .addCase(
        getSharedFocusesList.fulfilled,
        (state, { payload: { alerts, analysisAlertSettings, categories, countries, focuses } }) => {
          state.sharedFocuses = focuses.map(focus =>
            mapFocusDataToFocus(focus, {
              alerts,
              analysisAlertSettings,
              categories,
              countries,
            })
          );
        }
      )
      .addCase(deleteFocusAlert.fulfilled, (state, { payload }) => {
        const focusItem =
          state.focuses.find(focus => focus.id === payload) ||
          state.sharedFocuses.find(focus => focus.id === payload);

        if (focusItem) {
          focusItem.emailNotifications = false;
          focusItem.emailFrequency = undefined;
        }
      })
      .addMatcher(
        isAnyOf(createFocusAlert.fulfilled, updateFocusAlert.fulfilled),
        (state, { payload }) => {
          const focusItem =
            state.focuses.find(focus => focus.id === payload.focus) ||
            state.sharedFocuses.find(focus => focus.id === payload.focus);

          let localTimeOfNotification: string | null = null;

          if (focusItem) {
            if (payload.timeOfNotification) {
              const [hour, minutes] = payload.timeOfNotification.split(':');

              localTimeOfNotification = dayjs()
                .utc()
                .hour(Number(hour))
                .minute(Number(minutes))
                .local()
                .format('HH:mm');
            }

            focusItem.emailNotifications = true;
            focusItem.emailFrequency = {
              day: payload.dayOfTheWeek,
              frequency: payload.notificationPreference,
              hour: localTimeOfNotification,
            };
          }
        }
      )
      .addMatcher(
        isAnyOf(createAnalysisAlertSetting.fulfilled, updateAnalysisAlertSetting.fulfilled),
        (state, { payload }) => {
          const focusItem =
            state.focuses.find(focus => focus.id === payload.focusId) ||
            state.sharedFocuses.find(focus => focus.id === payload.focusId);

          if (focusItem) {
            let updatedAiAnalystTime: string | undefined;
            let updatedAiAnalystDay: FocusNotificationDayOfWeek | undefined = payload.dayOfTheWeek;

            if (payload.timeOfNotification) {
              const timeMatch = payload.timeOfNotification.match(/^(\d{1,2}):(\d{1,2})/);

              if (timeMatch) {
                const utcDayIndex = payload.dayOfTheWeek
                  ? daysForWeekdays.indexOf(payload.dayOfTheWeek)
                  : undefined;
                let aiAlertTime = dayjs().utc();

                if (utcDayIndex !== undefined && utcDayIndex !== -1) {
                  aiAlertTime = aiAlertTime.day(utcDayIndex);
                }

                aiAlertTime = aiAlertTime.hour(Number(timeMatch[1])).minute(Number(timeMatch[2]));
                const aiAlertTimeLocal = aiAlertTime.local();

                updatedAiAnalystTime = aiAlertTimeLocal.format('HH:mm');
                updatedAiAnalystDay =
                  toFocusNotificationDayOfWeek(aiAlertTimeLocal.format('dddd')) ?? undefined;
              }
            }

            focusItem.hasAnalysisAlertSetting = true;
            focusItem.aiAnalystReports = payload.receiveEmail;
            focusItem.aiAnalystFrequency = payload.notificationPreference;
            focusItem.aiAnalystTime = updatedAiAnalystTime;
            focusItem.aiAnalystDay = updatedAiAnalystDay;
          }
        }
      ),
});

export const {
  selectFocus,
  openFocusModal,
  saveDraftFocus,
  clearDraftFocus,
  updateFocusAiAnalystSettings,
} = focusesSlice.actions;

export default focusesSlice.reducer;
